import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import Button from "@material-ui/core/Button";
import { makeStyles } from "@material-ui/core/styles";
import { Backdrop, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, Paper, TextField } from "@material-ui/core";
import { deepCopy, getPerspective, mps2kt } from "../../utils/FPEUtils";
import axios from "axios";
import Draggable from 'react-draggable';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { useATCPlan } from "../../store/ATCPlanState";
import { highlightPointsState } from "../../store/HighlightPointsState";
import { useRecoilState, useSetRecoilState } from "recoil";
import { mapPerspectiveState } from "../../store/MapPerspectiveState";
import { getCycleDate } from "../../Authorization";
import { useAlert } from "../../hooks/useAlert";

const useStyles = makeStyles((theme) => ({
  margin: {
    margin: theme.spacing(1),
  },
  container: {
    maxHeight: "calc(100% - 46px)",
    width: "100%"
  },
  gridView: {
    height: 400,
    width: "100%"
  },
  gridViewFull: {
    height: "100%",
    width: "100%"
  }
}));

const planEditReducer = (state, action) => {
  switch (action.type) {
    case "RESET_EDIT": {
      return initializer(action.payload);
    }
    case "UPDATE_ROW":
      return {
        ...state,
        route: action.payload.updateRoute
      }
    default:
      return state;
  }
}
const initializer = (editJson) => {
  return deepCopy(editJson);
}

const PaperComponent = (props) => {
  return (
    <Draggable handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper {...props} />
    </Draggable>
  );
}

const alignDigits = (value, digit) => {
  return value.toLocaleString("en-US", {
    minimumFractionDigits: digit,
    maximumFractionDigits: digit
  });
}

const getPlanRoute = (p1, p2, p3) => {
  return new Promise((resolve, reject) => {
    const params = {
      fix: p2.name,
      lon: p2.lon,
      lat: p2.lat,
      alt: p2.level,
      fix1: p1.name,
      lon1: p1.lon,
      lat1: p1.lat,
      alt1: p1.level
    };
    if (p3) {
      Object.assign(params, {
        fix2: p3.name,
        lon2: p3.lon,
        lat2: p3.lat,
        alt2: p3.level
      });
    }
    Object.assign(params, { cycleDate: getCycleDate() });
    axios.get("cgi/plan_route.cgi",
      {
        params: params
      })
      .then(response => resolve(response.data))
      .catch(e => {
        console.log(`Failed to apply the route : ${e.message} \n parameter : ${JSON.stringify(params)}`);
        reject(e);
      });
  });
}

const restructRoute = (plan, editPoint, cancel) => {
  const promises = [];
  promises.push(
    new Promise(resolve => {
      let updateRoute = deepCopy(plan.route);

      const nearFix = (index, direction) => {
        let i = index;
        let delta;
        if(direction > 0 || direction === true) {
          delta = 1;
        } else {
          delta = -1;
        }

        while(true) {
          i += delta;
          if(delta === 1 && i >= updateRoute.length) {
            return null;
          }
          if(delta === -1 && i < 0) {
            return null;
          }
    
          if(!updateRoute[i].airway) {
            return updateRoute[i];
          }
        }
      };

      let routeIdx = editPoint.routeIdx;
      const prevPoint = nearFix(routeIdx, -1);
      const nextPoint = nearFix(routeIdx, +1);

      const routeSearch = {
        prev: false,
        next: false
      };
      let prevAirway = null;
      let nextAirway = null;
      let currentPoint = null;
      if (editPoint.airway) {
        const currentRoute = updateRoute[routeIdx];
        currentPoint = currentRoute.airway[editPoint.airwayIdx];
        currentPoint.level = editPoint.level;

        prevAirway = deepCopy(currentRoute);
        prevAirway.airway = prevAirway.airway.slice(0, editPoint.airwayIdx);
        const prevPrevPoint = prevAirway.airway.slice(-1)[0];
        const beforePoint = deepCopy(currentPoint);
        beforePoint.minAlt = prevPrevPoint.minAlt;
        beforePoint.maxAlt = prevPrevPoint.maxAlt;
        prevAirway.airway.push(beforePoint);

        nextAirway = deepCopy(currentRoute);
        nextAirway.airway = nextAirway.airway.slice(editPoint.airwayIdx + 1);
        const afterPoint = deepCopy(currentPoint);
        nextAirway.airway.unshift(afterPoint);
        afterPoint.dist = 0;
        afterPoint.time = 0;

        currentPoint.dist = 0;
        currentPoint.time = 0;

        updateRoute.splice(routeIdx, 1, prevAirway, currentPoint, nextAirway);
        routeIdx++;
      } else {
        currentPoint = updateRoute[routeIdx];
        currentPoint.level = editPoint.level;
        if (routeIdx > 0 && updateRoute[routeIdx - 1].airway) {
          prevAirway = updateRoute[routeIdx - 1];
          const beforePoint = prevAirway.airway.slice(-1)[0];
          beforePoint.level = editPoint.level;
        }
        if (routeIdx < updateRoute.length - 1 && updateRoute[routeIdx + 1].airway) {
          nextAirway = updateRoute[routeIdx + 1];
          const afterPoint = nextAirway.airway[0];
          afterPoint.level = editPoint.level;
        }
      }

      if (prevAirway === null || cancel.prev) {
        routeSearch.prev = true;
        if (prevAirway !== null) {
          updateRoute.splice(routeIdx - 1, 1);
          routeIdx--;
        }
      }
      if (nextAirway === null || cancel.next) {
        routeSearch.next = true;
        if (nextAirway !== null) {
          updateRoute.splice(routeIdx + 1, 1);
        }
      }

      let points = new Array(3);
      points[1] = currentPoint;

      if (routeSearch.prev) {
        points[0] = prevPoint;
      }
      if (routeSearch.next) {
        points[2] = nextPoint;
      }

      const targetPoints = points.filter(v => v);
      if (targetPoints.length >= 2) {
        promises.push(
          getPlanRoute.apply(null, targetPoints)
          .then(data => {
            data.forEach((route, i) => {
              let dist;
              if (route.airway) {
                let offset;
                if (i === 1) {
                  offset = 1;
                } else {
                  if (!points[0]) {
                    offset = 1;
                  } else {
                    offset = 0;
                  }
                }
                updateRoute.splice(routeIdx + offset, 0, route);
                if (offset === 0) {
                  routeIdx++;
                }
                let latestSpeed = targetPoints[0].speed;
                route.airway.forEach((a, n, array) => {
                  let level, levelOpt, speed;
                  if (n !== array.length - 1) {
                    level = targetPoints[i].level;
                    levelOpt = targetPoints[i].levelOpt;
                    speed = targetPoints[i].speed;
                  } else {
                    level = targetPoints[i + 1].level;
                    levelOpt = targetPoints[i + 1].levelOpt;
                    speed = targetPoints[i + 1].speed;
                  }
                  a.level = level;
                  a.speed = speed;
                  a.time = a.dist / latestSpeed;
                  if (levelOpt !== undefined) {
                    a.levelOpt = levelOpt;
                  }
                  latestSpeed = speed;
                });
                dist = 0;
              } else {
                dist = route.dist;
              }
              targetPoints[i + 1].dist = dist;
              targetPoints[i + 1].time = dist / targetPoints[i].speed;
            });
          })
        );
      }
      resolve(updateRoute);

    })
  );

  return Promise.all(promises)
    .then(values => values.filter(v => !!v)[0]);
}

const GridView = (props) => {
  const {fullScreen, resized} = props;
  const {atcPlan, changeATCPlanEdit} = useATCPlan();
  const [highlightPoints, setHighlightPoints] = useRecoilState(highlightPointsState);
  const setMapPerspective = useSetRecoilState(mapPerspectiveState);
  const [rows, setRows] = useState([]);
  const [editPlan, planDispatch] = useReducer(planEditReducer, atcPlan.edit.json, initializer);
  const [open, setOpen] = useState(false);
  const [loding, setLoding] = useState(false);
  const [editPoint, setEditPoint] = useState({});
  const [editNextPoint, setEditNextPoint] = useState({});
  const [editPrevPoint, setEditPrevPoint] = useState({});
  const classes = useStyles();
  const hoverPoint = useRef({});
  const {showSuccess, showError} = useAlert();

  useEffect(() => {
    if(0 < highlightPoints.length) {
      for(const fix of highlightPoints) {
        if(fix.source === "gridView") {
          return;
        }
        const row = rows.find(row => {
          const name = row.fixName?row.fixName:row.name;
          return name === fix.name && row.lon === fix.lon && row.lat === fix.lat;
        });
        if(row) {
          document.getElementById(`table-row${row.id}`).classList.add('row-highLight');
        }
      }
    } else {
      rows.forEach(row=>document.getElementById(`table-row${row.id}`).classList.remove('row-highLight'));
    }
  }, [highlightPoints, rows])

  useEffect(() => {
    planDispatch({type:'RESET_EDIT', payload: atcPlan.edit.json})
  }, [atcPlan.edit.json]);

  useEffect(() => {
    if (!editPlan.route) return;
    let idx = 0;

    let prevIsAway = false;
    let prevAwayDist = null;
    let prevAwayTime = null;
    let latestFix = null;
    let lastAirwayFix = null;
    const gridRows = editPlan.route.flatMap((route, routeIdx) => {
      if (route.airway) {
        prevIsAway = true;
        return route.airway.reduce((list, airway, airwayIdx,) => {
          idx++;
          const fix = {
            id: idx,
            routeIdx:routeIdx,
            airwayIdx: airwayIdx,
            airway: true,
            name: `${route.airwayName} : ${airway.name}`,
            airwayName: route.airwayName,
            fixName: airway.name,
            lon: airway.lon,
            lat: airway.lat,
            level: airway.level,
            dist: airway.dist,
            speed: airway.speed,
            minAlt: airway.minAlt,
            maxAlt: airway.maxAlt,
            time: airway.time
          };
          if (airwayIdx === 0 || airwayIdx === route.airway.length - 1) {
            if (airwayIdx === 0 && latestFix !== null) {
              latestFix.nextAirway = fix;
            }
            if (airwayIdx === route.airway.length - 1) {
              prevAwayDist = airway.dist;
              prevAwayTime = airway.time;
              lastAirwayFix = fix;
            }
            return list;
          } else {
            lastAirwayFix = null;
            list.push(fix);
          }
          return list;
        }, []);
      } else {
        idx++;
        const ret = {
          id: idx,
          routeIdx: routeIdx,
          name: route.name,
          lon: route.lon,
          lat: route.lat,
          level: route.level,
          dist: prevIsAway ? prevAwayDist : route.dist,
          speed: route.speed,
          time: prevIsAway ? prevAwayTime : route.time
        }
        if (lastAirwayFix !== null) {
          ret.prevAirway = lastAirwayFix;
          lastAirwayFix = null;
        }
        prevIsAway = false;
        latestFix = ret;
        return ret;
      }
    });
    const dep = editPlan.dep;
    gridRows.unshift({
      id: 0,
      name: dep.icao,
      lon: dep.lon,
      lat: dep.lat,
      level: '-',
      dist: '-',
      speed: '-',
      time: 0
    });
    idx++;
    const arr = editPlan.arr;
    gridRows.push({
      id: idx,
      name: arr.icao,
      lon: arr.lon,
      lat: arr.lat,
      level: '-',
      dist: arr.dist,
      speed: '-',
      time: arr.time
    });
    setRows(gridRows);
  }, [editPlan]);

  const selectedRow = useCallback((e) => {
    const id = parseInt(e.currentTarget.dataset.id);
    const row = rows.find(r => r.id === id);
    setEditPoint(row);
    const next = rows.find(r => {return r.id === id + 1}) || {};
    setEditNextPoint(next);
    const prev = rows.find(r => {return r.id === id - 1}) || {};
    setEditPrevPoint(prev);
    setOpen(true);
  }, [rows]);

  const dispatchHighlightPoint = e => {
    const fix = [];
    if(e.currentTarget.dataset.id) {
      const id = parseInt(e.currentTarget.dataset.id);
      const row = rows.find(r => r.id === id) || {};
      const tempFix = {name: row.fixName?row.fixName:row.name, lon: row.lon, lat: row.lat, index: row.id, source: "gridView", type: "fix"};
      if(hoverPoint.current.name === tempFix.name && hoverPoint.current.lon === tempFix.lon && hoverPoint.current.lat === tempFix.lat) {
        return;
      }
      hoverPoint.current = tempFix;
      fix.push(tempFix);
    } else {
      hoverPoint.current = {};
    }
    setHighlightPoints(fix);
  };

  const handleSave = useCallback(() => {
    const params = new URLSearchParams();
    params.append('plan', JSON.stringify(editPlan));
    params.append('orig', atcPlan.original.text);
    params.append('cycleDate', getCycleDate());
    axios.post('cgi/format.cgi', params).then(res => {
      changeATCPlanEdit({text: res.data.atc_plan, json: editPlan});
      setMapPerspective(getPerspective(editPlan));
      showSuccess('Applied to ATC Plan', {timeout: 2000, close: false});
    }).catch(error => {
      showError('Failed to convert to ATC', {timeout: 5000});
      console.log(`Failed to convert to ATC : ${error.message} \n parameter : ${params.toString()}`)
    });
  }, [setMapPerspective, editPlan, atcPlan.original.text, changeATCPlanEdit, showSuccess, showError]);

  const handleReset = useCallback(() => {
    planDispatch({type:'RESET_EDIT', payload: atcPlan.edit.json})
  }, [atcPlan.edit.json]);

  const handleChange = useCallback((event) => {
    const target = event.target;
    const value = parseFloat(target.value) * 100;
    setEditPoint({...editPoint, [target.id]: value});
    if (target.id === 'speed') {
      setEditNextPoint({...editNextPoint, time: editNextPoint.dist / value});
    }
  }, [editPoint, editNextPoint]);

  const handleSet = useCallback(() => {
    const alt = editPoint.level;
    let cancel = {
      prev: false,
      next: false
    };
    if (editPoint.airway) {
      if (alt < editPrevPoint.minAlt || alt > editPrevPoint.maxAlt) {
        cancel.prev = true;
      }
      if (alt < editPoint.minAlt || alt > editPoint.maxAlt) {
        cancel.next = true;
      }
    }
    if (editPoint.prevAirway && (alt < editPoint.prevAirway.minAlt || alt > editPoint.prevAirway.maxAlt)) {
      cancel.prev = true;
    }
    if (editPoint.nextAirway && (alt < editPoint.nextAirway.minAlt || alt > editPoint.nextAirway.maxAlt)) {
      cancel.next = true;
    }
    if (Object.values(cancel).includes(true)) {
      const message = [
        "Flight Level is out of range.",
        "Is it OK?"
      ].join("\n");
      if(!window.confirm(message)) {
        return false;
      }
    }
    setOpen(false);
    setLoding(true);
    restructRoute(editPlan, editPoint, cancel)
      .then(route => {
        // console.log(JSON.stringify(route, "", "\t"));
        planDispatch({type: "UPDATE_ROW", payload: {updateRoute: route}});
        setLoding(false);
      })
      .catch(() => {
        showError('Update Error.', {timeout: 5000})
        setLoding(false);
      });
  }, [editPlan, editPoint, editPrevPoint, showError]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <div className={fullScreen || resized ? classes.gridViewFull : classes.gridView}>
      <div>
        <Button variant="outlined" color="primary" size="small" className={classes.margin} onClick={handleSave}>
          Apply
        </Button>
        <Button variant="outlined" color="secondary" size="small" className={classes.margin} onClick={handleReset}>
          Reset
        </Button>
      </div>
      <TableContainer component={Paper} className={classes.container}>
        <Table className={classes.table} aria-label="simple table" stickyHeader >
          <TableHead>
            <TableRow>
              <TableCell>name</TableCell>
              <TableCell align="right">Alt(FL)</TableCell>
              <TableCell align="right">TAS(kt)</TableCell>
              <TableCell align="right">Lon</TableCell>
              <TableCell align="right">Lat</TableCell>
            </TableRow>
          </TableHead>
          <TableBody onMouseLeave={dispatchHighlightPoint}>
            {rows.length !== 0? rows.map((row) => (
              <TableRow key={row.name}
                hover
                onClick={selectedRow}
                onMouseOver={dispatchHighlightPoint}
                data-id={row.id}
                id={`table-row${row.id}`}
              >
                <TableCell component="th" scope="row">{row.name}</TableCell>
                <TableCell align="right">{isNaN(row.level)?row.level:alignDigits(row.level / 100, 0)}</TableCell>
                <TableCell align="right">{isNaN(row.speed)?row.speed:alignDigits(mps2kt(row.speed), 0)}</TableCell>
                <TableCell align="right">{alignDigits(row.lon, 3)}</TableCell>
                <TableCell align="right">{alignDigits(row.lat, 3)}</TableCell>
              </TableRow>
            )):<TableRow><TableCell colSpan="7" align="center">no rows</TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={editPoint.level !== '-' && open} onClose={handleClose} aria-labelledby="draggable-dialog-title" maxWidth='xs'
        PaperComponent={PaperComponent}>
        <DialogTitle id="draggable-dialog-title">{editPoint.name}</DialogTitle>
        <DialogContent>
          <TextField
            id="level"
            label="Altitude(FL)"
            type="number"
            defaultValue={alignDigits(editPoint.level / 100, 0)}
            disabled={editPoint.level === '-'}
            autoFocus
            margin="none"
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start">FL</InputAdornment>,
            }}
            onChange={handleChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleSet} color="primary">
            Set
          </Button>
        </DialogActions>
      </Dialog>
      <Backdrop className="grid" open={loding}>
        <CircularProgress/>
      </Backdrop>
    </div>
  )
}

export default GridView;
